import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  PawPrint, MapPin, Heart, Bell, Check, Calendar,
  Syringe, Weight, Newspaper, Home, Map as MapIcon, User, Wifi, BatteryFull,
} from "lucide-react";
import { LogoMark } from "./Logo";

const DOG_IMG =
  "https://images.unsplash.com/photo-1537204696486-967f1b7198c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGRvZyUyMHBvcnRyYWl0fGVufDB8fHx8MTc4ODg5OTIwOHww&ixlib=rb-4.1.0&q=85&w=300";
const PARK_IMG =
  "https://images.unsplash.com/photo-1607696412329-265a4103b433?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHw0fHxkb2clMjBydW5uaW5nJTIwcGFya3xlbnwwfHx8fDE3ODg4OTkyMDl8MA&ixlib=rb-4.1.0&q=85&w=300";
const HUG_IMG =
  "https://images.unsplash.com/photo-1557495235-340eb888a9fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxkb2clMjBhbmQlMjBvd25lciUyMGh1Z2dpbmd8ZW58MHx8fHwxNzg4ODk5MjA5fDA&ixlib=rb-4.1.0&q=85&w=300";

const StatusBar = () => (
  <div className="flex items-center justify-between px-6 pt-3 pb-1 text-[10px] font-semibold text-[#1A1A1A]">
    <span>9:41</span>
    <div className="flex items-center gap-1.5">
      <Wifi className="w-3 h-3" />
      <BatteryFull className="w-3.5 h-3.5" />
    </div>
  </div>
);

const AppHeader = ({ title }) => (
  <div className="flex items-center justify-between px-5 pt-2 pb-3">
    <div className="flex items-center gap-2">
      <LogoMark className="w-6 h-6" />
      <span className="font-display font-bold text-sm text-[#1A1A1A]">{title}</span>
    </div>
    <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#FF6B35]/10">
      <Bell className="w-3.5 h-3.5 text-[#FF6B35]" />
    </span>
  </div>
);

const TabBar = ({ active }) => {
  const tabs = [
    { icon: Home, key: "home" },
    { icon: PawPrint, key: "cane" },
    { icon: MapIcon, key: "mappa" },
    { icon: Newspaper, key: "news" },
  ];
  return (
    <div className="absolute bottom-0 inset-x-0 bg-white/95 backdrop-blur border-t border-[#1A1A1A]/[0.06] flex justify-around py-2.5">
      {tabs.map(({ icon: Icon, key }) => (
        <span
          key={key}
          className={`flex items-center justify-center w-9 h-9 rounded-full ${
            active === key ? "bg-[#FF6B35] text-white" : "text-[#5C5C5C]/50"
          }`}
        >
          <Icon className="w-4 h-4" />
        </span>
      ))}
    </div>
  );
};

const ScreenHome = () => (
  <div className="px-5 space-y-3 pb-16">
    <p className="font-display font-bold text-lg text-[#1A1A1A]">Ciao, Marta!</p>
    <div className="bg-[#FF6B35] rounded-2xl p-3.5 text-white flex items-center gap-3">
      <img src={DOG_IMG} alt="Billo" className="w-12 h-12 rounded-full object-cover border-2 border-white/60" />
      <div className="min-w-0">
        <p className="font-bold text-sm">Billo</p>
        <p className="text-[11px] text-white/80">Labrador · 3 anni</p>
      </div>
      <Heart className="w-4 h-4 ml-auto fill-white" />
    </div>
    <div className="bg-white rounded-2xl border border-[#1A1A1A]/[0.08] p-3.5 flex items-center gap-3">
      <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-[#FF6B35]/10">
        <Syringe className="w-4 h-4 text-[#FF6B35]" />
      </span>
      <div>
        <p className="text-xs font-bold text-[#1A1A1A]">Vaccino antirabbica</p>
        <p className="text-[10px] text-[#5C5C5C]">Tra 3 giorni · Dr. Rossi</p>
      </div>
      <span className="ml-auto text-[9px] font-bold uppercase tracking-wider text-[#FF6B35] bg-[#FF6B35]/10 rounded-full px-2 py-1">Promemoria</span>
    </div>
    <div className="bg-white rounded-2xl border border-[#1A1A1A]/[0.08] p-3.5">
      <div className="flex items-center gap-2.5">
        <img src={HUG_IMG} alt="Giulia e Thor" className="w-8 h-8 rounded-full object-cover" />
        <div>
          <p className="text-[11px] font-bold text-[#1A1A1A]">Giulia & Thor</p>
          <p className="text-[9px] text-[#5C5C5C]">2h fa · Parco Sempione</p>
        </div>
      </div>
      <p className="text-[11px] text-[#5C5C5C] mt-2 leading-relaxed">
        Chi viene alla passeggiata di gruppo sabato mattina? 🐕
      </p>
      <div className="flex items-center gap-3 mt-2 text-[#FF6B35]">
        <span className="flex items-center gap-1 text-[10px] font-semibold"><Heart className="w-3 h-3 fill-[#FF6B35]" /> 24</span>
        <span className="text-[10px] font-semibold text-[#5C5C5C]">8 commenti</span>
      </div>
    </div>
  </div>
);

const ScreenCane = () => (
  <div className="px-5 space-y-3 pb-16">
    <div className="flex flex-col items-center pt-1">
      <div className="relative">
        <img src={DOG_IMG} alt="Profilo di Billo" className="w-20 h-20 rounded-full object-cover border-4 border-[#FF6B35]/20" />
        <span className="absolute -bottom-1 -right-1 flex items-center justify-center w-6 h-6 rounded-full bg-[#FF6B35] border-2 border-[#F9F8F6]">
          <PawPrint className="w-3 h-3 text-white" />
        </span>
      </div>
      <p className="font-display font-bold text-base text-[#1A1A1A] mt-2">Billo</p>
      <p className="text-[10px] text-[#5C5C5C]">Labrador Retriever · Microchip OK</p>
    </div>
    <div className="grid grid-cols-3 gap-2">
      {[
        { icon: Weight, v: "28 kg", l: "Peso" },
        { icon: Calendar, v: "3 anni", l: "Età" },
        { icon: Syringe, v: "5/5", l: "Vaccini" },
      ].map(({ icon: Icon, v, l }) => (
        <div key={l} className="bg-white rounded-xl border border-[#1A1A1A]/[0.08] p-2.5 text-center">
          <Icon className="w-3.5 h-3.5 text-[#FF6B35] mx-auto" />
          <p className="text-xs font-bold text-[#1A1A1A] mt-1">{v}</p>
          <p className="text-[9px] text-[#5C5C5C]">{l}</p>
        </div>
      ))}
    </div>
    <p className="text-[10px] font-bold uppercase tracking-widest text-[#5C5C5C] pt-1">Libretto sanitario</p>
    {[
      { t: "Antirabbica", d: "12 ott 2026", done: false },
      { t: "Trivalente", d: "Fatta · 4 mar 2026", done: true },
      { t: "Antiparassitario", d: "Fatta · 1 set 2026", done: true },
    ].map((r) => (
      <div key={r.t} className="bg-white rounded-xl border border-[#1A1A1A]/[0.08] px-3.5 py-2.5 flex items-center gap-3">
        <span className={`flex items-center justify-center w-6 h-6 rounded-full ${r.done ? "bg-[#FF6B35]" : "bg-[#FF6B35]/10"}`}>
          <Check className={`w-3 h-3 ${r.done ? "text-white" : "text-[#FF6B35]"}`} />
        </span>
        <p className="text-[11px] font-semibold text-[#1A1A1A]">{r.t}</p>
        <p className={`text-[9px] ml-auto ${r.done ? "text-[#5C5C5C]" : "text-[#FF6B35] font-bold"}`}>{r.d}</p>
      </div>
    ))}
  </div>
);

const ScreenMappa = () => (
  <div className="absolute inset-0 top-[72px]">
    <div
      className="absolute inset-0"
      style={{
        background:
          "linear-gradient(rgba(255,107,53,0.05), rgba(255,107,53,0.05)), repeating-linear-gradient(0deg, transparent, transparent 34px, rgba(26,26,26,0.05) 34px, rgba(26,26,26,0.05) 35px), repeating-linear-gradient(90deg, transparent, transparent 34px, rgba(26,26,26,0.05) 34px, rgba(26,26,26,0.05) 35px), #F1EFE9",
      }}
    />
    <div className="absolute left-0 right-0 top-1/3 h-2 bg-white/70 rotate-6" />
    <div className="absolute top-0 bottom-0 left-1/2 w-2 bg-white/70 -rotate-12" />
    {[
      { x: "22%", y: "22%", label: "Luna" },
      { x: "64%", y: "16%", label: "Rocky" },
      { x: "74%", y: "48%", label: "Milo" },
      { x: "30%", y: "58%", label: "Thor" },
    ].map((p) => (
      <div key={p.label} className="absolute flex flex-col items-center" style={{ left: p.x, top: p.y }}>
        <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#1A1A1A] border-2 border-white shadow-md">
          <PawPrint className="w-3 h-3 text-[#FF6B35]" />
        </span>
        <span className="text-[8px] font-bold text-[#1A1A1A] bg-white/90 rounded-full px-1.5 py-0.5 mt-1 shadow-sm">{p.label}</span>
      </div>
    ))}
    <div className="absolute flex flex-col items-center" style={{ left: "46%", top: "34%" }}>
      <span className="pulse-dot flex items-center justify-center w-9 h-9 rounded-full bg-[#FF6B35] border-[3px] border-white shadow-lg">
        <MapPin className="w-4 h-4 text-white" />
      </span>
      <span className="text-[8px] font-bold text-white bg-[#FF6B35] rounded-full px-1.5 py-0.5 mt-1">Tu & Billo</span>
    </div>
    <div className="absolute bottom-14 inset-x-4 bg-white rounded-2xl border border-[#1A1A1A]/[0.08] shadow-lg p-3 flex items-center gap-3">
      <img src={PARK_IMG} alt="Parco Sempione" className="w-11 h-11 rounded-xl object-cover" />
      <div className="min-w-0">
        <p className="text-xs font-bold text-[#1A1A1A]">Parco Sempione</p>
        <p className="text-[10px] text-[#5C5C5C]">12 cani nelle vicinanze · 400 m</p>
      </div>
      <span className="ml-auto shrink-0 bg-[#FF6B35] text-white text-[9px] font-bold rounded-full px-2.5 py-1.5">Vai</span>
    </div>
  </div>
);

const ScreenNews = () => (
  <div className="px-5 space-y-3 pb-16">
    <p className="font-display font-bold text-lg text-[#1A1A1A]">News</p>
    {[
      { img: PARK_IMG, tag: "Eventi", t: "Sabato al parco: raduno FuffyFam a Milano", d: "Oltre 50 cani iscritti" },
      { img: HUG_IMG, tag: "Salute", t: "Caldo e zampe: 5 regole per l'estate", d: "A cura della Dr.ssa Rossi" },
      { img: DOG_IMG, tag: "Community", t: "Billo è il cane del mese!", d: "Votato da 320 famiglie" },
    ].map((a) => (
      <div key={a.t} className="bg-white rounded-2xl border border-[#1A1A1A]/[0.08] overflow-hidden">
        <div className="flex items-stretch">
          <img src={a.img} alt={a.t} className="w-20 object-cover" />
          <div className="p-3 min-w-0">
            <span className="text-[8px] font-bold uppercase tracking-widest text-[#FF6B35]">{a.tag}</span>
            <p className="text-[11px] font-bold text-[#1A1A1A] leading-snug mt-0.5">{a.t}</p>
            <p className="text-[9px] text-[#5C5C5C] mt-1">{a.d}</p>
          </div>
        </div>
      </div>
    ))}
  </div>
);

const SCREENS = [
  { key: "home", label: "Home", component: ScreenHome, tab: "home" },
  { key: "cane", label: "Profilo cane", component: ScreenCane, tab: "cane" },
  { key: "mappa", label: "Mappa", component: ScreenMappa, tab: "mappa" },
  { key: "news", label: "News", component: ScreenNews, tab: "news" },
];

export const AppPreview = () => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setActive((a) => (a + 1) % SCREENS.length), 4000);
    return () => clearInterval(t);
  }, []);

  const Current = SCREENS[active].component;

  return (
    <section
      data-testid="app-preview"
      className="relative px-6 sm:px-10 lg:px-16 py-24 lg:py-36 border-b border-[#1A1A1A]/[0.08] overflow-hidden"
    >
      <div className="absolute -top-24 left-1/3 w-[36rem] h-[36rem] rounded-full bg-[#FF6B35]/[0.07] blur-3xl pointer-events-none" />

      <div className="relative grid lg:grid-cols-2 gap-16 lg:gap-12 items-center max-w-7xl mx-auto">
        <div className="order-2 lg:order-1 flex justify-center">
          <motion.div
            initial={{ opacity: 0, y: 60, rotate: 4 }}
            whileInView={{ opacity: 1, y: 0, rotate: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="absolute -inset-6 rounded-[4rem] bg-[#FF6B35]/15 rotate-3 hidden sm:block" />
            <div
              data-testid="phone-mockup"
              className="relative w-[280px] sm:w-[300px] h-[580px] sm:h-[620px] bg-[#1A1A1A] rounded-[3rem] p-[10px] shadow-[0_40px_100px_rgba(26,26,26,0.25)]"
            >
              <div className="relative w-full h-full bg-[#F9F8F6] rounded-[2.5rem] overflow-hidden">
                <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-24 h-6 bg-[#1A1A1A] rounded-full z-20" />
                <StatusBar />
                <AppHeader title="FuffyFam" />
                <div className="relative h-[calc(100%-130px)]">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={SCREENS[active].key}
                      initial={{ x: 90, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      exit={{ x: -90, opacity: 0 }}
                      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                      className="absolute inset-0"
                      data-testid={`app-screen-${SCREENS[active].key}`}
                    >
                      <Current />
                    </motion.div>
                  </AnimatePresence>
                </div>
                <TabBar active={SCREENS[active].tab} />
              </div>
            </div>
          </motion.div>
        </div>

        <div className="order-1 lg:order-2">
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="text-xs sm:text-sm uppercase tracking-[0.25em] font-bold text-[#FF6B35]"
          >
            L'app in anteprima
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            data-testid="app-preview-title"
            className="font-display font-bold mt-4 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#1A1A1A]"
          >
            Tutta FuffyFam, <span className="text-[#FF6B35]">in tasca.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 text-base sm:text-lg leading-relaxed text-[#5C5C5C] max-w-lg"
          >
            Un assaggio delle schermate principali: il profilo del tuo cane, la mappa dei
            nuovi amici vicino a te e le news della community. Lascia la tua email per provarla per primo.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 flex flex-wrap gap-3"
            data-testid="app-preview-dots"
          >
            {SCREENS.map((s, i) => (
              <button
                key={s.key}
                onClick={() => setActive(i)}
                data-testid={`app-preview-tab-${s.key}`}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                  active === i
                    ? "bg-[#1A1A1A] text-[#F9F8F6]"
                    : "bg-white border border-[#1A1A1A]/10 text-[#5C5C5C] hover:border-[#FF6B35] hover:text-[#FF6B35]"
                }`}
              >
                {s.label}
              </button>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-8 flex items-center gap-2 text-sm font-semibold text-[#5C5C5C]"
          >
            <span className="w-2 h-2 rounded-full bg-[#FF6B35] pulse-dot" />
            In arrivo su iOS e Android nel 2026
          </motion.div>
        </div>
      </div>
    </section>
  );
};
